import { useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { BootstrapData, Employee } from "@czasomat/contracts";
import { Download, MoreHorizontal, Plus, Printer, Search, Upload, UserRoundCheck, UserRoundX } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { patch, post } from "../api/client";
import { DataTable, type Column } from "../components/DataTable";
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Field,
  FilterGroup,
  FilterSidebar,
  FormModal,
  IconButton,
  Modal,
  PageHeader,
  Toast
} from "../components/ui";

type StatusFilter = "all" | "active" | "inactive";

export function TeamPage({ data }: { data: BootstrapData }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [positionIds, setPositionIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [message, setMessage] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const defaultEmployeeRoleId = data.roles.some((role) => role.id === "role_employee") ? "role_employee" : "";
  const canPrintTeamCards = data.auth.permissions.includes("all");
  const activeEmployees = data.employees.filter((employee) => employee.active);
  const qrCards = canPrintTeamCards ? activeEmployees.flatMap((employee) => {
    const badge = data.qrBadges.find((item) => item.employeeId === employee.id && item.active && typeof item.previewToken === "string" && item.previewToken.trim().length > 0);
    return badge ? [{ employee, payload: `RCP:${employee.id}:${badge.previewToken}` }] : [];
  }) : [];
  const missingCards = activeEmployees.length - qrCards.length;

  const filtered = useMemo(() => data.employees.filter((employee) => {
    const haystack = `${employee.firstName} ${employee.lastName} ${employee.employeeCode}`.toLocaleLowerCase("pl");
    if (search && !haystack.includes(search.toLocaleLowerCase("pl"))) return false;
    if (status === "active" && !employee.active) return false;
    if (status === "inactive" && employee.active) return false;
    if (roleIds.length && !roleIds.includes(employee.roleId)) return false;
    if (tagIds.length && !employee.tagIds.some((tagId) => tagIds.includes(tagId))) return false;
    if (positionIds.length && (!employee.positionId || !positionIds.includes(employee.positionId))) return false;
    return true;
  }), [data.employees, positionIds, roleIds, search, status, tagIds]);

  const createEmployee = useMutation({
    mutationFn: (payload: Record<string, unknown>) => post<Employee>("/api/v1/employees", payload),
    onSuccess: async () => {
      setAddOpen(false);
      setMessage("Pracownik został dodany.");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => patch(`/api/v1/employees/${id}`, { active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["bootstrap"] })
  });

  const submitEmployee = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    createEmployee.mutate({
      firstName: form.get("firstName"),
      lastName: form.get("lastName"),
      email: form.get("email"),
      phone: form.get("phone"),
      employeeCode: form.get("employeeCode"),
      roleId: form.get("roleId"),
      positionId: form.get("positionId") || null,
      tagIds: form.getAll("tagIds"),
      pin: form.get("pin") || undefined
    });
  };

  const importCsv = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!defaultEmployeeRoleId) return setMessage("Przed importem skonfiguruj rolę pracownika. Osoby możesz dodać ręcznie, wybierając rolę.");
    const lines = (await file.text()).split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) return setMessage("Plik nie zawiera danych pracowników.");
    const delimiter = lines[0]!.includes(";") ? ";" : ",";
    const headers = lines[0]!.split(delimiter).map((item) => item.trim().toLowerCase());
    let imported = 0;
    for (const line of lines.slice(1)) {
      const values = line.split(delimiter).map((item) => item.trim());
      const row = Object.fromEntries(headers.map((header, index) => [header, values[index] || ""]));
      const firstName = row.imie || row.firstname;
      const lastName = row.nazwisko || row.lastname;
      if (!firstName || !lastName) continue;
      await post("/api/v1/employees", {
        firstName,
        lastName,
        email: row.email || "",
        phone: row.telefon || row.phone || "",
        employeeCode: row.kod || row.employeecode || `IMP-${Date.now()}-${imported}`,
        roleId: defaultEmployeeRoleId,
        positionId: null,
        tagIds: []
      });
      imported += 1;
    }
    setMessage(`Zaimportowano pracowników: ${imported}.`);
    await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    event.target.value = "";
  };

  const toggleFilter = (list: string[], id: string, setter: (value: string[]) => void) => setter(list.includes(id) ? list.filter((value) => value !== id) : [...list, id]);
  const toggleSelected = (id: string) => setSelected((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);

  const columns: Column<Employee>[] = [
    { key: "select", label: <input type="checkbox" aria-label="Zaznacz wszystkich" checked={filtered.length > 0 && selected.length === filtered.length} onChange={(event) => setSelected(event.target.checked ? filtered.map((item) => item.id) : [])} />, width: "42px", render: (employee) => <input type="checkbox" aria-label={`Zaznacz ${employee.firstName} ${employee.lastName}`} checked={selected.includes(employee.id)} onChange={() => toggleSelected(employee.id)} onClick={(event) => event.stopPropagation()} /> },
    { key: "name", label: "Imię i nazwisko", width: "36%", render: (employee, index) => <div className="employee-cell"><Avatar initials={employee.initials} colorIndex={index} /><div><strong>{employee.firstName} {employee.lastName}</strong><span>{employee.email || employee.employeeCode}</span></div></div> },
    { key: "role", label: "Rola i tagi", width: "34%", render: (employee) => <div className="tag-list"><Badge>{data.roles.find((role) => role.id === employee.roleId)?.name || "Bez roli"}</Badge>{employee.tagIds.map((tagId) => { const tag = data.tags.find((item) => item.id === tagId); return tag ? <Badge key={tagId}>{tag.name}</Badge> : null; })}</div> },
    { key: "position", label: "Stanowisko", width: "22%", render: (employee) => data.positions.find((position) => position.id === employee.positionId)?.name || <span className="muted">Bez stanowiska</span> },
    { key: "actions", label: "", width: "54px", align: "right", render: (employee) => <IconButton label={employee.active ? "Dezaktywuj pracownika" : "Aktywuj pracownika"} onClick={(event) => { event.stopPropagation(); toggleActive.mutate({ id: employee.id, active: !employee.active }); }}>{employee.active ? <UserRoundX size={17} /> : <UserRoundCheck size={17} />}</IconButton> }
  ];

  return (
    <div className="page-with-sidebar">
      <FilterSidebar open={sidebarOpen} onToggle={() => setSidebarOpen((value) => !value)} title="Zespół">
        <label className="search-field"><Search size={16} /><input placeholder="Szukaj" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
        <FilterGroup title="Pracownicy">
          <Checkbox label="Wszyscy" checked={status === "all"} onChange={() => setStatus("all")} />
          <Checkbox label="Aktywni" checked={status === "active"} onChange={() => setStatus("active")} />
          <Checkbox label="Zdezaktywowani" checked={status === "inactive"} onChange={() => setStatus("inactive")} />
        </FilterGroup>
        <FilterGroup title="Role">{data.roles.map((role) => <Checkbox key={role.id} label={role.name} checked={roleIds.includes(role.id)} onChange={() => toggleFilter(roleIds, role.id, setRoleIds)} />)}</FilterGroup>
        <FilterGroup title="Tagi">{data.tags.map((tag) => <Checkbox key={tag.id} label={tag.name} checked={tagIds.includes(tag.id)} onChange={() => toggleFilter(tagIds, tag.id, setTagIds)} />)}</FilterGroup>
        <FilterGroup title="Stanowiska">{data.positions.map((position) => <Checkbox key={position.id} label={position.name} checked={positionIds.includes(position.id)} onChange={() => toggleFilter(positionIds, position.id, setPositionIds)} />)}</FilterGroup>
      </FilterSidebar>

      <section className="page-surface">
        <PageHeader
          title="Twój zespół"
          count={filtered.length}
          subtitle={selected.length ? `${selected.length} zaznaczonych` : undefined}
          actions={<>
            <Button variant="primary" compact icon={<Plus size={16} />} onClick={() => setAddOpen(true)}>Dodaj</Button>
            <Button compact icon={<Upload size={16} />} onClick={() => fileRef.current?.click()}>Importuj</Button>
            {canPrintTeamCards ? <Button compact icon={<Printer size={16} />} onClick={() => setQrOpen(true)}>Drukuj karty QR</Button> : null}
            <IconButton label="Więcej działań"><MoreHorizontal size={18} /></IconButton>
            <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(event) => void importCsv(event)} />
          </>}
        />
        <div className="content-table-band">
          <DataTable columns={columns} rows={filtered} emptyTitle="Brak pracowników spełniających filtry" rowClassName={(employee) => employee.active ? undefined : "is-muted"} />
        </div>
      </section>

      {addOpen ? (
        <FormModal title="Dodaj pracownika" onClose={() => setAddOpen(false)} onSubmit={submitEmployee} busy={createEmployee.isPending}>
          <div className="form-avatar-row"><Avatar initials="?" size="large" /><p>Dane podstawowe pracownika</p></div>
          <Field label="Imię"><input name="firstName" required autoFocus /></Field>
          <Field label="Nazwisko"><input name="lastName" required /></Field>
          <Field label="Adres e-mail"><input name="email" type="email" /></Field>
          <Field label="Telefon"><input name="phone" type="tel" /></Field>
          <Field label="Kod pracownika"><input name="employeeCode" required placeholder="np. A-1004" /></Field>
          <Field label="PIN (4–8 cyfr)"><input name="pin" inputMode="numeric" pattern="[0-9]{4,8}" /></Field>
          <Field label="Rola"><select name="roleId" required defaultValue={defaultEmployeeRoleId}><option value="" disabled>Wybierz rolę</option>{data.roles.map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}</select></Field>
          <Field label="Stanowisko"><select name="positionId"><option value="">Bez stanowiska</option>{data.positions.map((position) => <option key={position.id} value={position.id}>{position.name}</option>)}</select></Field>
          <fieldset className="field field--full"><legend className="field__label">Tagi</legend><div className="choice-grid">{data.tags.map((tag) => <label key={tag.id}><input type="checkbox" name="tagIds" value={tag.id} />{tag.name}</label>)}</div></fieldset>
          {createEmployee.error ? <p className="form-error field--full">{createEmployee.error instanceof Error ? createEmployee.error.message : "Błąd zapisu"}</p> : null}
        </FormModal>
      ) : null}

      {qrOpen && canPrintTeamCards ? (
        <Modal title="Karty QR pracowników" onClose={() => setQrOpen(false)} width="860px" footer={<><Button icon={<Download size={16} />} disabled={!qrCards.length} onClick={() => window.print()}>Drukuj / PDF</Button><Button variant="primary" onClick={() => setQrOpen(false)}>Gotowe</Button></>}>
          {missingCards > 0 ? <p>Aktywni pracownicy bez dostępnej aktywnej karty QR: {missingCards}. Ich karty pominięto w wydruku.</p> : null}
          {!qrCards.length ? <p>Brak aktywnych kart QR do wydrukowania.</p> : null}
          <div className="qr-grid">{qrCards.map(({ employee, payload }) => <article className="qr-badge" key={employee.id}><QRCodeSVG value={payload} size={132} level="M" /><strong>{employee.firstName} {employee.lastName}</strong><span>{employee.employeeCode}</span></article>)}</div>
        </Modal>
      ) : null}

      {message ? <Toast message={message} /> : null}
    </div>
  );
}
